import { useEffect, useRef, type ReactNode } from 'react';
import { cva } from 'class-variance-authority';
import { Flex, Skeleton } from '@nula/core-ui';
import { isReadableTextSurface } from '../utils/template-sources';
import { useDocumentEditor } from '@/lib/documents/document-editor-context';
import { useCanvasPreviewRenderer } from '@/lib/canvas/canvas-preview-context';
import { surfaceDisplayStatus, type SurfaceEntry } from '@/lib/sync/document-surfaces';
import type { PendingStage } from '@/stores/use-template-run-store';
import { useTemplate } from '@/lib/templates/use-templates-store';
import {
  regeneratingDetail,
  regeneratingHeadline,
  regenerationFailedCopy,
} from '@/lib/templates/review-copy';
import type { SummarizeOffer } from '../hooks/use-template-runs';
import type { ProposalState } from '../hooks/use-proposal';
import { useFlipView } from '../hooks/use-flip-view';
import { SurfaceAudioBody } from './surface-audio-body';
import { SurfaceFailedBody } from './surface-failed-body';
import { SurfaceGeneratingBody } from './surface-generating-body';
import { SurfaceProposalBar } from './surface-proposal-bar';
import { SurfaceReviewDiagramBar, SurfaceReviewDiagramProposal } from './surface-review-diagram';
import { SurfaceTextReview } from './surface-text-review';
import styles from './surface-body.module.css';

const noop = () => {};
const UNTITLED = ['Untitled'];
const NO_PROPOSAL: ProposalState = { kind: 'none' };

const canvasSlots = cva(styles.canvasSlots, {
  variants: {
    layout: { single: '', flip: styles.flip },
  },
  defaultVariants: { layout: 'single' },
});

function CanvasSlots({ review, children }: { review: boolean; children: ReactNode }) {
  return <Flex className={canvasSlots({ layout: review ? 'flip' : 'single' })}>{children}</Flex>;
}

interface SurfaceBodyProps {
  entityId: string;
  entry: SurfaceEntry;
  /** Whether the viewer can edit at all — independent of `entry.editable`
   *  (a per-surface generator lock). Either one makes the bound editor
   *  read-only. Defaults to true. */
  canEdit?: boolean;
  /** What the surface read, for the copy that names it. */
  sourceTitles?: readonly string[];
  viewerIsRequester?: boolean;
  /** Stage of this device's run matched to the surface; only `failed` matters. */
  matchedRunStage?: PendingStage | null;
  onTryAgain?: () => void;
  onRemove?: () => void;
  /** For a recording surface: the offer to summarize it (ui-design.md §3.4). */
  summarize?: SummarizeOffer;
  proposal?: ProposalState;
  reviewing?: boolean;
  templateName?: string | null;
  onReview?: () => void;
  onDismiss?: () => void;
  onRegenerate?: () => void;
  onReviewClosed?: (settled: boolean) => void;
  onProposalReplaced?: () => void;
  onPartiallyApplied?: (proposalId: string) => void;
  /** This client just closed this proposal; the proposed bars stay hidden. */
  bannerHidden?: boolean;
  bannerBusy?: boolean;
  /** Why this client's last Dismiss or Try again was refused. */
  bannerMessage?: string | null;
}

interface BannerInput {
  entry: SurfaceEntry;
  status: 'ready' | 'regenerating' | 'proposed';
  canEdit: boolean;
  sourceTitles: readonly string[];
  proposal: ProposalState;
  templateName?: string | null;
  onReview?: () => void;
  onDismiss?: () => void;
  onRegenerate?: () => void;
  bannerHidden: boolean;
  bannerBusy: boolean;
  bannerMessage: string | null;
}

function SurfaceBanner({
  entry,
  status,
  canEdit,
  sourceTitles,
  proposal,
  templateName,
  onReview,
  onDismiss,
  onRegenerate,
  bannerHidden,
  bannerBusy,
  bannerMessage,
}: BannerInput) {
  const template = useTemplate(entry.generator);
  const outputType = entry.surface === 'canvas' ? 'canvas' : 'text';
  if (status === 'regenerating') {
    return (
      <SurfaceProposalBar
        kind="regenerating"
        canEdit={canEdit}
        headline={regeneratingHeadline(outputType, templateName ?? template?.name ?? null)}
        detail={regeneratingDetail(entry.attempt)}
      />
    );
  }
  if (status === 'proposed') {
    if (bannerHidden) return null;
    if (!canEdit) {
      return (
        <SurfaceProposalBar
          kind="waiting"
          canEdit={false}
          templateName={template?.name ?? null}
          fromExcerpt={false}
        />
      );
    }
    if (proposal.kind === 'ready') {
      return (
        <SurfaceProposalBar
          kind="waiting"
          canEdit
          templateName={templateName ?? template?.name ?? null}
          fromExcerpt={proposal.proposal.fromExcerpt}
          onReview={onReview}
          onDismiss={onDismiss}
          busy={bannerBusy}
          message={bannerMessage}
        />
      );
    }
    if (proposal.kind === 'stale') {
      return (
        <SurfaceProposalBar kind="stale" canEdit onDismiss={onDismiss} busy={bannerBusy} message={bannerMessage} />
      );
    }
    if (proposal.kind === 'error') {
      return (
        <SurfaceProposalBar
          kind="waiting"
          canEdit
          templateName={templateName ?? template?.name ?? null}
          fromExcerpt={false}
          onReview={onReview}
          onDismiss={onReview ? undefined : onDismiss}
          busy={bannerBusy}
          message={bannerMessage}
        />
      );
    }
    return null;
  }
  if (!entry.regenerationFailure) return null;
  const copy = regenerationFailedCopy({
    failure: entry.regenerationFailure,
    outputType,
    templateName: template?.name ?? null,
    sourceTitles,
    viewerIsRequester: false,
    canEdit,
    surfaceTitle: entry.title,
  });
  return (
    <SurfaceProposalBar
      kind="regeneration-failed"
      copy={copy}
      canEdit={canEdit}
      onTryAgain={onRegenerate}
      onDismiss={onDismiss}
      busy={bannerBusy}
      message={bannerMessage}
    />
  );
}

/**
 * Renders one fragment-backed surface, binding the injected editor to the
 * entry's `fragment`.
 *
 * `key={entry.fragment}` is load-bearing. @tiptap/react 3.23.6 binds
 * Collaboration's `field` at construction: `fragmentName` is absent from
 * `useEditor`'s dep array, and `setOptions` rebuilds neither the extension
 * manager nor the schema. Without a fresh instance per surface, switching
 * surfaces leaves the previous surface's content on screen.
 */
export function SurfaceBody({
  entityId,
  entry,
  canEdit = true,
  sourceTitles = UNTITLED,
  viewerIsRequester = false,
  matchedRunStage = null,
  onTryAgain,
  onRemove = noop,
  summarize,
  proposal = NO_PROPOSAL,
  reviewing = false,
  templateName,
  onReview,
  onDismiss,
  onRegenerate,
  onReviewClosed = noop,
  onProposalReplaced = noop,
  onPartiallyApplied = noop,
  bannerHidden = false,
  bannerBusy = false,
  bannerMessage = null,
}: SurfaceBodyProps) {
  const Editor = useDocumentEditor();
  const CanvasPreview = useCanvasPreviewRenderer();
  const contentRef = useRef<HTMLElement>(null);
  const wasReviewing = useRef(reviewing);
  useEffect(() => {
    if (wasReviewing.current && !reviewing) contentRef.current?.focus();
    wasReviewing.current = reviewing;
  }, [reviewing]);
  const { shown, choose } = useFlipView(
    reviewing && proposal.kind === 'ready' && proposal.proposal.outputType === 'canvas'
      ? proposal.proposal.proposalId
      : null,
  );
  if (entry.generator === 'recording') {
    return (
      <SurfaceAudioBody
        entityId={entityId}
        surfaceId={entry.id}
        recordingId={entry.recordingId}
        canEdit={canEdit}
        summarize={summarize}
      />
    );
  }
  const status = surfaceDisplayStatus(entry);
  if (status === 'failed' || (status === 'generating' && matchedRunStage === 'failed')) {
    const failedEntry = status === 'failed' ? entry : { ...entry, failure: undefined };
    return (
      <SurfaceFailedBody
        entry={failedEntry}
        sourceTitles={sourceTitles}
        canEdit={canEdit}
        viewerIsRequester={viewerIsRequester}
        onTryAgain={onTryAgain}
        onRemove={onRemove}
      />
    );
  }
  if (status === 'generating') {
    return <SurfaceGeneratingBody entry={entry} sourceTitles={sourceTitles} />;
  }
  const readOnly = !entry.editable || !canEdit;
  const diagramReview =
    reviewing && proposal.kind === 'ready' && proposal.proposal.outputType === 'canvas' ? proposal.proposal : null;
  const textReview =
    reviewing && proposal.kind === 'ready' && proposal.proposal.outputType === 'text' ? proposal.proposal : null;
  let content: ReactNode;
  if (entry.surface === 'canvas') {
    const concealed = diagramReview !== null && shown !== 'current';
    const proposedCanvas: ReactNode = diagramReview && (
      <SurfaceReviewDiagramProposal entityId={entityId} surfaceId={entry.id} concealed={shown !== 'new'} />
    );
    content = (
      <CanvasSlots review={diagramReview !== null}>
        <Flex
          data-review-canvas={diagramReview ? true : undefined}
          data-concealed={diagramReview ? String(concealed) : undefined}
          className={styles.canvasColumn}
        >
          {CanvasPreview ? (
            <CanvasPreview
              key={entry.fragment}
              entityId={entityId}
              entityKindHint="document"
              nested
              nestedKey={entry.fragment}
              readOnly={readOnly || concealed}
            />
          ) : (
            <Skeleton />
          )}
        </Flex>
        {proposedCanvas}
      </CanvasSlots>
    );
  } else if (Editor) {
    content = (
      <Editor
        key={entry.fragment}
        entityId={entityId}
        entityKindHint="document"
        fragmentName={entry.fragment}
        readOnly={readOnly}
        showTitle={false}
        showActionbar={false}
        offerGenerateFromSelection={textReview === null && isReadableTextSurface(entry)}
        suspended={textReview !== null}
      />
    );
  } else {
    content = <Skeleton />;
  }
  return (
    <Flex ref={contentRef} tabIndex={-1} className={styles.content}>
      {diagramReview ? (
        <SurfaceReviewDiagramBar
          entityId={entityId}
          surfaceId={entry.id}
          proposal={diagramReview}
          templateName={templateName ?? null}
          view={shown}
          onViewChange={choose}
          onClosed={onReviewClosed}
          onReplaced={onProposalReplaced}
        />
      ) : textReview ? (
        <SurfaceTextReview
          key={entry.id}
          entityId={entityId}
          surfaceId={entry.id}
          proposal={textReview}
          templateName={templateName ?? null}
          onClosed={onReviewClosed}
          onReplaced={onProposalReplaced}
          onPartiallyApplied={onPartiallyApplied}
        />
      ) : (
        <SurfaceBanner
          entry={entry}
          status={status}
          canEdit={canEdit}
          sourceTitles={sourceTitles}
          proposal={proposal}
          templateName={templateName}
          onReview={onReview}
          onDismiss={onDismiss}
          onRegenerate={onRegenerate}
          bannerHidden={bannerHidden}
          bannerBusy={bannerBusy}
          bannerMessage={bannerMessage}
        />
      )}
      {content}
    </Flex>
  );
}
